import {
	getLocalTimeZone,
	now,
	parseAbsoluteToLocal,
	parseDateTime,
	toLocalTimeZone,
	toZoned,
	type ZonedDateTime,
} from '@internationalized/date';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';

import type {
	Action as ProcessAction,
	TriggerSpec,
} from '../../../../../bridge/types/action.ts';
import type {
	Artifact,
	ExpressMetadata,
	InstagramPost,
	InstagramPostSchedule,
} from '../../../../../bridge/types/artifact.ts';
import type { ProcessStatus } from '../../../../../bridge/types/process.ts';

// A stored absolute time this close to now was never a time the customer chose.
// It is what an older "Publish now" checkbox wrote before the relative schedule
// existed. Anything further out is a real pick, and it stays untouched.
export const SCHEDULE_THRESHOLD_MS = 2 * 60 * 1000;

export const rowThumbnailClass =
	'relative w-[100px] shrink-0 self-stretch min-h-[100px] overflow-hidden rounded-lg border border-gray-200 bg-white';

export function futureDefaultDate() {
	return now(getLocalTimeZone()).add({ days: 1 }).set({ hour: 9, minute: 0 });
}

// A picked post time is schedulable only when it clears SCHEDULE_THRESHOLD_MS. A
// time inside that window is read back as a publish-now marker by
// isPublishNowSentinel, so the panel must never store one as a chosen time.
//
// `leadMs` exists because that rule belongs to a POST and not to an email. An
// email row writes a `scheduled_once` trigger, and no reader treats a near one
// as "send now" — `isPublishNowSentinel` takes an InstagramPostSchedule and
// nothing else. An email row therefore passes 0, which leaves the only real
// rule: the moment has not already passed. The Send-email dialog uses that same
// rule, so the two surfaces now give one answer to the same pick.
export function isSchedulableTime(
	wanted: Date,
	leadMs: number = SCHEDULE_THRESHOLD_MS,
): boolean {
	return wanted.getTime() > Date.now() + leadMs;
}

// Stored times have no timezone, so keep every write in local wall-clock form.
export function absoluteScheduleFrom(date: Date): InstagramPostSchedule {
	const m = moment(date);
	return {
		kind: 'absolute',
		on: m.format('YYYY-MM-DD'),
		at: m.format('HH:mm:ss'),
	};
}

// The smallest gap between now and the publish date handed to Express. It
// absorbs the Express round trip and a small difference between the browser
// clock and the Express clock.
export const MIN_PUBLISH_MARGIN_MS = 60 * 1000;

// Express publishes on whole minutes and the resolver only runs when the
// customer opens the app, so a post can be legitimately late by a few minutes.
// This is the point where a customer should be told the post is late. It never
// accuses the post on its own: past this point the verdict reads `unconfirmed`,
// and only the server's counted 404s or SCHEDULE_ABANDONED_MS reach `past_due`.
export const PAST_DUE_GRACE_MS = 15 * 60 * 1000;

// True when a scheduled instant has passed by more than the grace window. A row
// with no readable date is never past due, which keeps the tile and the detail
// callout from disagreeing about a schedule neither can read.
export function isSchedulePastDue(iso: string | null | undefined): boolean {
	if (!iso) return false;
	const at = new Date(iso).getTime();
	if (Number.isNaN(at)) return false;
	return at + PAST_DUE_GRACE_MS < Date.now();
}

// What Bridgeway can honestly say about a scheduled post. `unconfirmed` is the
// state between the two: the publish instant has passed, and nothing yet proves
// the post either published or was lost.
export type SchedulePublishState = 'on_time' | 'unconfirmed' | 'past_due';

// Mirrors NOT_FOUND_MAX_ATTEMPTS in beacon at
// api/routes/instagram/resolve_media_ids.py:34. The browser cannot read that
// value, so the two numbers must be kept equal by hand.
export const NOT_FOUND_MAX_ATTEMPTS = 3;

// Mirrors NOT_FOUND_PAST_DUE_GRACE in beacon at
// api/routes/instagram/resolve_media_ids.py:37. The browser cannot read that
// value, so the two numbers must be kept equal by hand.
//
// The server retires a post on two conditions, not one: enough counted 404s AND
// this much time past the post's own instant. It counts a 404 on an interval
// alone, so a post can carry the full count before it is even due. Inside this
// window the server is still asking Express, and Express still answers
// `published` for posts it has 404ed before.
export const NOT_FOUND_PAST_DUE_GRACE_MS = 60 * 60 * 1000;

// The point past which no plausible publish window is still open. The server
// counter is the better proof, but the server only counts a look when the
// customer opens the app, and many late posts carry no record at all. So the
// clock is the second path into `past_due`.
export const SCHEDULE_ABANDONED_MS = 24 * 60 * 60 * 1000;

// The verdict on a scheduled post, from its instant and what Bridgeway knows
// about the Adobe Express post behind it. It keys on the content of the
// resolution record, never on its presence: every status the server writes
// describes a post that is still healthy, and a missing record means nothing
// has looked yet. So a post only reads `past_due` once the server has counted
// enough 404s AND stopped asking Express about it, or once so much time has
// passed that no publish window is still open.
export function schedulePublishState(
	iso: string | null | undefined,
	express: ExpressMetadata | null | undefined,
): SchedulePublishState {
	if (!iso || !isSchedulePastDue(iso)) return 'on_time';
	// No Express post id means Adobe Express never took this post. So nothing is
	// going to publish it, and nothing is going to confirm it either. beacon's
	// resolver reads only rows that carry a post id: its query requires
	// IS_DEFINED(c.express_metadata.post_id) at
	// api/routes/instagram/resolve_media_ids.py:284. That resolver holds the only
	// path from `scheduled` to `published` (resolve_media_ids.py:474). So no
	// resolution record can ever arrive to settle this post.
	//
	// `unconfirmed` promised the customer a confirmation that cannot come, and
	// held it for SCHEDULE_ABANDONED_MS. Three paths write this state: both
	// browser schedule paths, when Express answers with no post id
	// (PostContentDetail.tsx:473, ProcessSchedulePanel.tsx:1029), and a chat
	// approval (services/py/shared/models/artifact_types.py:235).
	if (!express?.post_id) return 'past_due';
	const resolution = express.media_id_resolution;
	// Express reports this post as published. It is not lost, whatever the clock
	// says, so it never reaches `past_due`.
	if (resolution?.status === 'published_incomplete') return 'unconfirmed';
	const lateBy = Date.now() - new Date(iso).getTime();
	// The server's rule has two halves, and the badge needs both. A `not_found`
	// record with no counter predates the counter and stands for one attempt, the
	// same rule the server applies. The count alone is not proof: the server goes
	// on asking Express until NOT_FOUND_PAST_DUE_GRACE_MS past the instant, so
	// inside that window the post is one the server still expects to publish.
	if (
		resolution?.status === 'not_found' &&
		(resolution.attempts ?? 1) >= NOT_FOUND_MAX_ATTEMPTS &&
		lateBy > NOT_FOUND_PAST_DUE_GRACE_MS
	) {
		return 'past_due';
	}
	return lateBy > SCHEDULE_ABANDONED_MS ? 'past_due' : 'unconfirmed';
}

// True when a picked time has already gone by. This is a strict comparison
// against the clock with no grace window at all, which is what a picker needs:
// PAST_DUE_GRACE_MS answers "should we call this post late", and
// SCHEDULE_THRESHOLD_MS answers "did the customer choose this time at all".
// Neither of those is the question here.
export function isZonedDateTimePast(value: ZonedDateTime): boolean {
	return value.toDate().getTime() <= Date.now();
}

// True when the server will read this send time as already gone by.
//
// `_handle_activation_triggers` in api/routes/processes.py truncates the send
// time and its own clock to the UTC minute, then holds the send when the send
// minute is not later. `isZonedDateTimePast` compares whole instants, so the two
// disagree for the seconds inside the current minute and the panel promises a
// send the server is about to hold. Epoch minutes are UTC minutes, so flooring
// the millisecond value is the same truncation.
export function isSendMinutePast(value: ZonedDateTime): boolean {
	const startOfMinute = (ms: number): number =>
		Math.floor(ms / 60_000) * 60_000;
	return startOfMinute(value.toDate().getTime()) <= startOfMinute(Date.now());
}

// Every picker that can hold a past time says this, so the owner reads the same
// sentence wherever they meet the problem.
export const PAST_PICK_MESSAGE =
	'This time has already passed. Pick a new one.';

// The instant a stored `scheduled_once` timestamp names, or null when nothing
// can read it.
//
// Many stored timestamps carry no zone at all, and `parseAbsoluteToLocal`
// rejects that shape outright. The server reads such a value as UTC and holds
// or sends on it, so the browser has to read it the same way: a browser that
// gives up here tells the owner a send is on its way while the server is
// holding it.
export function parseStoredTriggerTime(
	timestamp: string | null | undefined,
): ZonedDateTime | null {
	if (!timestamp) return null;
	// Normalised the way `_parse_action_timestamp` normalises it: stripped, and a
	// space separator read as `T`. `datetime.fromisoformat` accepts both shapes, so
	// the server stores and acts on them, while `parseDateTime` rejects both. Read
	// unchanged, a past send of either shape looked unreadable here. Every hold
	// affordance then stayed off and the panel promised a send the server held.
	const normalized = timestamp.trim().replace(' ', 'T');
	try {
		return parseAbsoluteToLocal(normalized);
	} catch {
		// Not an absolute instant. It may still be a zoneless wall time.
	}
	try {
		return toLocalTimeZone(toZoned(parseDateTime(normalized), 'UTC'));
	} catch {
		return null;
	}
}

// The error a schedule draft earns, or null when it is good.
//
// Read through `parseStoredTriggerTime`, so a draft that names no zone is judged
// as UTC — the reading the picker and the server both give it. Judged with
// `new Date` instead, a zoneless draft was read as local wall time, and a send
// time that had already gone by passed this check by the browser's own offset.
//
// `heldOnWholeMinute` picks the rule the draft's own consumer is judged by, and
// the two consumers differ. An email send goes to
// `_handle_activation_triggers`, which holds on the whole UTC minute, so a draft
// later in the current minute is still future by instant and the server holds it
// anyway. The draft carries live seconds — `getDefaultScheduleDraft` seeds it
// from `moment().add(1, 'minute')` — so that gap opens on an ordinary dwell, for
// as many seconds as the popover's opening second. An Express post is not judged
// that way: `sanitizeScheduleDateTimeForExpress` rounds the draft up to the next
// whole minute AFTER this check, so the instant rule is correct there and the
// minute rule would refuse a time Express would still accept.
export function scheduleDraftError(
	draft: string,
	{ heldOnWholeMinute = false }: { heldOnWholeMinute?: boolean } = {},
): string | null {
	if (!draft) return 'Please choose a date and time.';
	const parsed = parseStoredTriggerTime(draft);
	if (parsed === null) return 'Please choose a valid date and time.';
	const hasPassed = heldOnWholeMinute
		? isSendMinutePast(parsed)
		: isZonedDateTimePast(parsed);
	if (hasPassed) return 'Schedule must be in the future.';
	return null;
}

// The instant to hand Express for a post that publishes as soon as the campaign
// activates. Express rejects a date in the past and publishes on whole minutes,
// so take the first whole minute that is at least MIN_PUBLISH_MARGIN_MS out.
// Rounding up is what makes the margin a floor: rounding down would put the
// date under a minute away for half of all clock positions. The result always
// stays under SCHEDULE_THRESHOLD_MS, so a retry after a failed Express call
// still reads the stored row as a publish-now row.
export function resolveOnActivationPublishDate(): string {
	const earliest = moment().add(MIN_PUBLISH_MARGIN_MS, 'milliseconds');
	const wholeMinute = earliest.clone().startOf('minute');
	if (wholeMinute.isBefore(earliest)) wholeMinute.add(1, 'minute');
	return wholeMinute.toISOString();
}

// The instant a stored absolute schedule points at, or null when the row holds
// no readable date. An empty or unreadable row parses to an invalid moment,
// whose ISO form is null, and Express refuses a null publish date. Callers skip
// such a row instead of sending it.
export function absolutePublishDateIso(
	schedule: InstagramPostSchedule,
): string | null {
	if (schedule.kind !== 'absolute' || !schedule.on || !schedule.at) return null;
	const at = moment(`${schedule.on}T${schedule.at}`);
	return at.isValid() ? at.toISOString() : null;
}

// The instant a legacy row stores, when it stores one. That shape keeps the
// whole instant in `fire_at` and carries no `kind`, so every reader here reads
// it as no schedule at all: the panel labels the row publish-now, the Activate
// gate counts it unscheduled, and activation skips it. Return the instant so a
// caller can rewrite the row into the shape the other readers share. An
// unreadable value returns null and the row keeps whatever it holds.
export function legacyFireAtIso(
	schedule: InstagramPostSchedule | null | undefined,
): string | null {
	if (!schedule) return null;
	if (!('fire_at' in schedule) || !schedule.fire_at) return null;
	const at = moment(schedule.fire_at);
	return at.isValid() ? at.toISOString() : null;
}

// True when the stored schedule means "publish now" rather than a time the
// customer chose. The relative kind says so outright. An absolute time inside
// SCHEDULE_THRESHOLD_MS is an older write from the same checkbox, and it is
// resolved again at activation instead of being sent as-is.
export function isPublishNowSentinel(
	schedule: InstagramPostSchedule | null | undefined,
): boolean {
	if (!schedule) return false;
	if (schedule.kind === 'on_activation') return true;
	if (schedule.kind !== 'absolute') return false;
	const at = moment(`${schedule.on}T${schedule.at}`);
	if (!at.isValid()) return false;
	return at.valueOf() <= Date.now() + SCHEDULE_THRESHOLD_MS;
}

// True when two schedule times name the same minute. The duplicate-schedule
// guards compare on the minute because that is the finest unit anything here
// commits to: Express publishes on whole minutes, and both browser paths round
// a chosen time up to one. Comparing instants would miss a repeat that differs
// only by the milliseconds each press read off the clock. Both sides parse
// through moment, so a naive wall-clock string and an ISO instant that mean the
// same time still match.
export function isSameMinute(a: string, b: string): boolean {
	const left = moment(a);
	const right = moment(b);
	if (!left.isValid() || !right.isValid()) return false;
	return left.isSame(right, 'minute');
}

// Parsed through `parseStoredTriggerTime`, so the write half reads a zoneless
// value the same way the read half and the server do: as UTC. `new Date` reads
// it as local wall time instead, and an owner who opened the schedule editor and
// pressed Schedule without touching the picker stored the send at their own UTC
// offset away from the time the screen showed.
export function normalizeToUtcIsoZ(value: string): string {
	return parseStoredTriggerTime(value)?.toDate().toISOString() ?? value;
}

// Holds a schedule pick a caller has not stored yet. A date-and-time control
// takes a pick one field at a time, so a customer can pass through an invalid
// value (past, or too soon) before landing on one that sticks. Both
// `DateAndTimeFields` (ProcessSchedulePanel.tsx) and `EmailSchedule` need the
// same reconcile-on-external-change / notify-pending-parent behavior; this was
// previously two independently hand-copied trees of effects that could drift.
export function usePendingSchedulePick<T>(
	currentValueKey: number,
	onPendingChange?: (isPending: boolean) => void,
): readonly [T | null, (next: T | null) => void] {
	const [pending, setPending] = useState<T | null>(null);

	// The parent moved the row on its own: a toggle, or a re-derive from a fresh
	// value. A notice about a pick the customer can no longer see is its own
	// wrong statement about state.
	useEffect(() => {
		setPending(null);
	}, [currentValueKey]);

	// The callback arrives as an inline closure, so its identity changes on
	// every render of the caller. Depending on that identity would re-run both
	// effects below on every render, and the unmount cleanup would then report
	// `false` over the live value. Hold it in a ref so each effect fires on its
	// own trigger only.
	const notifyPending = useRef(onPendingChange);
	useEffect(() => {
		notifyPending.current = onPendingChange;
	}, [onPendingChange]);

	useEffect(() => {
		notifyPending.current?.(pending !== null);
	}, [pending]);

	// The field can leave while it still holds a refused pick: a toggle or a
	// dialog close can unmount it. A caller that blocks its submit on a held
	// pick would then block it forever.
	useEffect(() => () => notifyPending.current?.(false), []);

	return [pending, setPending] as const;
}

const TITLE_MAX_CHARS = 60;

export function scheduleItemTitle(artifact: Artifact): string {
	if (artifact.type === 'instagram_post') {
		const caption = artifact.content.caption ?? '';
		for (const line of caption.split('\n')) {
			const trimmed = line.trim();
			if (trimmed.length === 0) continue;
			return trimmed.length > TITLE_MAX_CHARS
				? trimmed.slice(0, TITLE_MAX_CHARS)
				: trimmed;
		}
		return 'Instagram post';
	}
	if (artifact.type === 'email_html' || artifact.type === 'email_plaintext') {
		const subject = (artifact.content as { subject?: string }).subject?.trim();
		return subject && subject.length > 0 ? subject : 'Email';
	}
	return artifact.user_summary?.trim() || 'Item';
}

export type ActivateMode =
	| 'campaign'
	| 'ig_publish'
	| 'ig_schedule'
	| 'email_send'
	| 'email_schedule';

export const PUBLISH_NOW_SUMMARY = 'ETA 2 mins';

const ACTIVATE_SUCCESS_TITLES: Record<ActivateMode, string> = {
	campaign: 'Campaign activated',
	ig_publish: 'Publishing now',
	ig_schedule: 'Scheduled',
	email_send: 'Sent',
	email_schedule: 'Scheduled',
};

export function activateSuccessTitle(mode: ActivateMode): string {
	return ACTIVATE_SUCCESS_TITLES[mode];
}

export function activateSuccessSummary(
	mode: ActivateMode,
	dynamicSummary: string,
	scheduledIso?: string | null,
): string {
	if (mode === 'ig_publish') return PUBLISH_NOW_SUMMARY;
	if (mode === 'ig_schedule' && scheduledIso) {
		const formatted = formatScheduleDate(scheduledIso);
		if (formatted) return `Publishes on ${formatted}`;
	}
	return dynamicSummary;
}

export function formatScheduleDate(iso: string): string {
	const m = moment(iso);
	if (!m.isValid()) return '';
	const sameYear = m.year() === moment().year();
	return sameYear
		? m.format('MMM D [at] h:mma')
		: m.format('MMM D, YYYY [at] h:mma');
}

// A stored schedule is free-form JSON and need not be an object at all: a legacy
// row can hold the instant as a bare string, a shape beacon still reads at
// api/routes/artifacts.py:320. `'field' in value` raises TypeError on a string,
// so test the shape before any optional-field read on a schedule.
export function isScheduleObject(
	schedule: InstagramPostSchedule | null | undefined,
): schedule is InstagramPostSchedule {
	return typeof schedule === 'object' && schedule !== null;
}

// A stored instant every caller can read as a date, or null. Callers parse the
// result with `new Date(...)`, and moment reads anything `new Date` reads, so
// this one test covers both the past-due rule and the date on the tile.
function readableInstant(value: unknown): string | null {
	if (typeof value !== 'string' || !value) return null;
	return Number.isNaN(new Date(value).getTime()) ? null : value;
}

// The wall-clock form of a stored `scheduled_once` send time, or '' when nothing
// can read it.
//
// Read through `parseStoredTriggerTime`, so a zoneless stored value is read as
// UTC — the reading `_parse_action_timestamp` gives it at
// api/routes/processes.py:714. `formatScheduleDate` parses with `moment`, which
// reads a zoneless value as local wall time instead, so it printed a send time
// the owner's own UTC offset away from the instant the server acts on. Near
// midnight it printed the wrong day.
export function formatTriggerSendTime(timestamp: string): string {
	const parsed = parseStoredTriggerTime(timestamp);
	if (parsed === null) return '';
	return formatScheduleDate(parsed.toDate().toISOString());
}

// A stored schedule is unvalidated JSON, so `on` and `at` can hold anything.
// `new Date(...).toISOString()` raises RangeError on an unreadable pair rather
// than answering a value, and this runs during render from several panels and
// inside the re-check effect, so one malformed row would blank a whole panel.
// absolutePublishDateIso parses the same pair with moment and answers null.
export function postScheduleIso(artifact: InstagramPost): string | null {
	const s = artifact.schedule;
	if (!s) return null;
	// A legacy row stores the instant as a bare string instead of an object.
	if (!isScheduleObject(s)) return readableInstant(s);
	if (s.kind === 'absolute') return absolutePublishDateIso(s);
	// An unreadable `fire_at` answers null for the same reason the absolute branch
	// does. Returned raw, a junk instant reads as `on_time` for ever, so the post
	// keeps a `Scheduled` label and never earns the past-due prompt.
	if ('fire_at' in s) return readableInstant(s.fire_at);
	return null;
}

const humanizeEvent = (event: string): string =>
	event.replace(/_/g, ' ').trim() || 'the previous step';

/**
 * The verb a dated one-off trigger has earned, from whatever it is dating.
 *
 * Only `scheduled_once` needs asking: every other trigger type reads the same
 * whatever drives it ("Every week", "As soon as post published"). An email sends
 * and an Instagram post publishes, and telling an owner their post "Sends Oct 2"
 * describes a thing the product does not do. With no artifact to read — an
 * independent action, or a caller that has none to hand — there is nothing to
 * infer a verb from, so the wording stays neutral rather than guessing.
 */
function scheduledOnceVerb(artifact: Artifact | null | undefined): string {
	if (artifact?.type === 'instagram_post') return 'Publishes';
	if (artifact?.type === 'email_html' || artifact?.type === 'email_plaintext') {
		return 'Sends';
	}
	return 'Scheduled for';
}

/**
 * When an action will run, read off its trigger.
 *
 * For a process that has not started, `statusToRowLabel` is the wrong instrument:
 * a `proposed` artifact matches none of its branches and falls through to
 * "Pending", which tells an owner being asked to approve the work precisely
 * nothing. Before anything runs, the true and useful fact is the schedule the
 * planner chose — and that lives on the action, not the artifact. Once the
 * process IS active, `statusToRowLabel` is right again and this should not be
 * used: it would keep promising a future for a post that already published.
 */
export function triggerSummary(
	trigger: TriggerSpec | null | undefined,
	/** What the action is bound to, where the caller knows it. Chooses the verb
	 * for a dated one-off; every other trigger type ignores it. */
	artifact?: Artifact | null,
): string {
	if (!trigger) return 'No schedule yet';
	switch (trigger.type) {
		case 'on_activation':
			return 'When this starts';
		case 'scheduled_once': {
			const at = parseStoredTriggerTime(trigger.timestamp);
			const when = formatTriggerSendTime(trigger.timestamp);
			if (at === null || !when) return 'Scheduled';
			// Only a mail send is held. `_handle_activation_triggers` skips every
			// action that is not `send_email_to_contacts` —
			// api/routes/processes.py:948. So a past-dated post still publishes and
			// must not read as waiting, while a past-dated email never sends until
			// the owner says so.
			const isEmail =
				artifact?.type === 'email_html' || artifact?.type === 'email_plaintext';
			if (isEmail && isSendMinutePast(at)) {
				return `Waiting for your OK — was set for ${when}`;
			}
			return `${scheduledOnceVerb(artifact)} ${when}`;
		}
		case 'scheduled_recurring': {
			const unit = trigger.unit || 'week';
			const every =
				trigger.every > 1 ? `Every ${trigger.every} ${unit}s` : `Every ${unit}`;
			const days = (trigger.day_of_week ?? []).filter(Boolean);
			const at = trigger.at?.trim();
			return [
				every,
				days.length ? days.join(', ') : null,
				at ? `at ${at}` : null,
			]
				.filter(Boolean)
				.join(' · ');
		}
		case 'event_delayed': {
			const { days, hours } = trigger.delay ?? { days: 0 };
			const gap = days
				? `${days} day${days === 1 ? '' : 's'}`
				: `${hours ?? 0}h`;
			return `${gap} after ${humanizeEvent(trigger.event)}`;
		}
		case 'event_immediate':
			return `As soon as ${humanizeEvent(trigger.event)}`;
		default:
			return 'No schedule yet';
	}
}

export type RowLabel = { label: string; muted?: boolean; sent?: boolean };

export function isItemSent(
	artifact: Artifact,
	action: ProcessAction | null,
): boolean {
	if (artifact.type === 'instagram_post') {
		return artifact.status === 'published';
	}
	if (artifact.type === 'email_html' || artifact.type === 'email_plaintext') {
		return action?.outputs?.send_status?.kind === 'sent';
	}
	return false;
}

export function statusToRowLabel(
	artifact: Artifact,
	action: ProcessAction | null,
	processStatus: ProcessStatus | string,
): RowLabel {
	if (processStatus === 'paused') {
		return { label: 'Paused', muted: true };
	}
	const isActive = processStatus === 'active';

	if (artifact.type === 'instagram_post') {
		const post = artifact;
		const iso = postScheduleIso(post);
		if (post.status === 'published') {
			return {
				label: iso ? `Published ${formatScheduleDate(iso)}` : 'Published',
				sent: true,
			};
		}
		if (post.status === 'failed') {
			return { label: 'Failed', muted: true };
		}
		if (post.status === 'cancelled') {
			return { label: 'Cancelled', muted: true };
		}
		if (post.status === 'scheduled' && iso) {
			switch (schedulePublishState(iso, post.express_metadata)) {
				case 'past_due':
					return {
						label: `Past due — was scheduled for ${formatScheduleDate(iso)}`,
						muted: true,
					};
				case 'unconfirmed':
					return {
						label: `Publishing — scheduled for ${formatScheduleDate(iso)}`,
					};
				default:
					return { label: `Scheduled for ${formatScheduleDate(iso)}` };
			}
		}
		if (!iso && isActive) {
			return { label: 'Awaiting schedule', muted: true };
		}
		return { label: 'Pending', muted: true };
	}

	if (artifact.type === 'email_html' || artifact.type === 'email_plaintext') {
		const trigger = action?.trigger ?? null;
		const sendStatus = action?.outputs?.send_status ?? null;

		if (sendStatus?.kind === 'sent') {
			return { label: `Sent ${formatScheduleDate(sendStatus.at)}`, sent: true };
		}
		if (trigger?.type === 'scheduled_once') {
			const at = parseStoredTriggerTime(trigger.timestamp);
			const when = formatTriggerSendTime(trigger.timestamp);
			if (at === null || !when) return { label: 'Scheduled', muted: true };
			// Judged on the whole UTC minute, the boundary the server holds by at
			// api/routes/processes.py:972. `new Date` read a zoneless send time as
			// local wall time, so a send the server had already held still read
			// "Sends on ...", and the past case promised a schedule instead of
			// naming the hold.
			return isSendMinutePast(at)
				? {
						label: `Waiting for your OK — was set for ${when}`,
						muted: true,
					}
				: { label: `Sends on ${when}` };
		}
		if (trigger?.type === 'on_activation' && isActive) {
			return { label: 'Awaiting send', muted: true };
		}
		return { label: 'Pending', muted: true };
	}

	return { label: 'Pending', muted: true };
}
